import type { ReactNode, Ref } from "react";
import React from "react";
import {
  type StyleProp,
  type TVParallaxProperties,
  type ViewStyle,
  type View,
  Platform,
} from "react-native";
import type { AnimatedStyle } from "react-native-reanimated";
import { useSharedValue } from "react-native-reanimated";

import Pressable from "$src/core/components/atoms/Pressable";
import { TV_PARALLAX } from "$src/core/constants/tv-parallax";

import $, { useAnimatedStyles } from "./SnapPickerItem.styles";

type SnapPickerItemProps = {
  width: number;
  isActive: boolean;
  onPress: () => void;
  accessibilityLabel?: string;
  accessibilityState?: { selected?: boolean };
  styleExtension?: StyleProp<ViewStyle | AnimatedStyle<ViewStyle>>;
  children: ReactNode;
  hasTVPreferredFocus?: boolean;
  onItemFocus?: () => void;
  onItemBlur?: () => void;
  tvParallaxProperties?: TVParallaxProperties;
  ref?: Ref<View>;
};

const SnapPickerItem = ({
  ref,
  width,
  isActive,
  onPress,
  accessibilityLabel,
  accessibilityState,
  styleExtension,
  children,
  hasTVPreferredFocus,
  onItemFocus,
  onItemBlur,
  tvParallaxProperties = TV_PARALLAX.default,
}: SnapPickerItemProps) => {
  const isFocused = useSharedValue(false);

  const { itemStyle } = useAnimatedStyles({ isFocused });

  return (
    <Pressable
      ref={ref}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      hasTVPreferredFocus={hasTVPreferredFocus}
      onPress={onPress}
      onFocus={() => {
        isFocused.set(true);
        onItemFocus?.();
      }}
      onBlur={() => {
        isFocused.set(false);
        onItemBlur?.();
      }}
      tvParallaxProperties={tvParallaxProperties}
      style={[
        $.item,
        { width },
        isActive ? $.active : $.hoverable,
        styleExtension,
        Platform.isTV && itemStyle,
      ]}
    >
      {children}
    </Pressable>
  );
};

export default SnapPickerItem;
